import { ReactElement, useEffect, useState } from 'react';
import { useCreateAccountMutation } from '../../services/user/user.queries';
import { emailRegexp } from '../../utils/config';
import { Button } from '../shared/button/Button';
import { TextInput } from '../shared/text-input/TextInput';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { SignupStep } from '../../constants/signup';
import styles from './CreateAccount.module.scss';
import { gtmTrackAccountCreated } from '../../services/gtm/gtm';
import { fetchCurrentUser } from '../../services/user/user.api';

export function EmailSignup(): ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const {
    cart,
    setCurrentSignupStep,
  } = useMenuWithCart();
  const isSubmitEnabled = !!email.length && !!password.length
    && !cart?.priceSummary.remainingMinimumAmount;
  const createAccountMutation = useCreateAccountMutation();

  useEffect(() => {
    try {
      const { turnstile } = window as any;
      const widgetId = turnstile.render('#turnstile-container', {
        sitekey: window.gon.cloudflare_sitekey,
        size: 'flexible',
        theme: 'light',
        callback(token) {
          console.log('Success:', token);
          setTurnstileToken(token);
        },
      });
    } catch (e) {
      console.error(e);
    }
  }, []);

  const verifyFormInputs = (): boolean => {
    let isEmailVerified = false;
    let isPasswordVerified = false;

    if (!emailRegexp.test(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
      isEmailVerified = true;
    }

    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
    } else {
      setPasswordError('');
      isPasswordVerified = true;
    }

    return isEmailVerified && isPasswordVerified;
  };

  const handleSubmit = async () => {
    const isValid = verifyFormInputs();

    if (!isValid) {
      return;
    }

    const signupStatus = await createAccountMutation.mutateAsync({
      email,
      password,
      turnstileToken,
    });

    if (signupStatus === 'email_taken') {
      setEmailError('This email is already associated with an account. Log in instead.');
    } else if (signupStatus === 'success') {
      document.cookie = `account_creation_email=${encodeURIComponent(email)}`;
      const currentUser = await fetchCurrentUser(true);
      gtmTrackAccountCreated(currentUser!);
      setCurrentSignupStep(SignupStep.SHIPPING_ADDRESS);
    }
  };

  return (
    <>
      <div className={styles.input_field}>
        <TextInput
          label="Email*"
          placeholder="Email"
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
          error={emailError}
        />
      </div>
      <div className={styles.input_field}>
        <TextInput
          label="Password*"
          placeholder="Password (minimum 8 characters)"
          value={password}
          onChange={(event) => setPassword(event.currentTarget.value)}
          error={passwordError}
          type="password"
        />
      </div>
      <div className={styles.input_field} id="turnstile-container" />
      <Button
        className={styles.full_width}
        data-heap-id="submit_email_signup"
        text="Sign Up with Email"
        onClick={handleSubmit}
        isEnabled={isSubmitEnabled}
      />
    </>
  );
}
